import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, matches, highlights } from '@/mocks/sportsData';
import type { Match } from '@/mocks/sportsData';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'football');
  const [filterStatus, setFilterStatus] = useState<'all' | 'live' | 'upcoming' | 'finished'>('all');

  useEffect(() => {
    if (slug) setActiveTab(slug);
    window.scrollTo(0, 0);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeTab);
  const categoryMatches: Match[] = matches.filter((m) => m.categorySlug === activeTab);
  const filteredMatches: Match[] = filterStatus === 'all'
    ? categoryMatches
    : categoryMatches.filter((m) => m.status === filterStatus);

  const pageTitle = `${currentCategory?.name || '赛事'}直播在线观看 - 每日大赛`;
  const pageDesc = currentCategory?.description || '高清赛事直播在线观看';

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`${currentCategory?.name || '赛事'}直播,每日大赛在线观看,高清${currentCategory?.name || '体育'}赛事直播,大赛直播在线观看`}
        canonicalUrl={`https://www.meiri-dasai.com/category/${activeTab}`}
      />
      <SchemaOrg type="WebPage" data={{ name: pageTitle, url: `https://www.meiri-dasai.com/category/${activeTab}`, isPartOf: { '@type': 'WebSite', name: '每日大赛在线观看' } }} />
      <SchemaOrg type="BreadcrumbList" data={{ itemListElement: [{ '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.meiri-dasai.com/' }, { '@type': 'ListItem', position: 2, name: currentCategory?.name || '赛事分类' }] }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-4 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: currentCategory?.name || '赛事分类' }]} />
          </div>
        </div>

        <main>
          {/* Category Hero */}
          <section className="py-8 md:py-12 px-4 md:px-6 bg-background-100">
            <div className="max-w-[1280px] mx-auto text-center">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100/70">
                <i className={`${currentCategory?.icon || 'ri-live-line'} text-3xl text-primary-500`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {currentCategory?.name || '赛事'}直播在线观看
              </h1>
              <p className="text-sm text-foreground-600 max-w-[600px] mx-auto">{currentCategory?.description}</p>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="py-6 px-4 md:px-6 bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveTab(cat.slug)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeTab === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Filter & Matches */}
          <section className="py-8 md:py-12 px-4 md:px-6 bg-background-50">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center gap-3 mb-6">
                {(['all', 'live', 'upcoming', 'finished'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      filterStatus === status
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {status === 'all' && '全部'}
                    {status === 'live' && '直播中'}
                    {status === 'upcoming' && '即将开始'}
                    {status === 'finished' && '已结束'}
                  </button>
                ))}
                <span className="text-xs text-foreground-500 ml-auto">共 {filteredMatches.length} 场赛事</span>
              </div>

              {filteredMatches.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">暂无相关赛事</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredMatches.map((match) => (
                    <Link
                      key={match.id}
                      to={`/article/${match.id}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img src={match.imageUrl} alt={`${match.teamA} vs ${match.teamB}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        {match.status === 'live' && (
                          <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500 text-white text-xs font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                            直播中
                          </div>
                        )}
                        {match.status === 'upcoming' && (
                          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium">
                            即将开始
                          </div>
                        )}
                        {match.status === 'finished' && (
                          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-foreground-700/70 text-white text-xs font-medium">
                            已结束
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">{match.league}</span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-foreground-900">{match.teamA}</span>
                          {match.scoreA !== null ? (
                            <span className="text-lg font-heading font-bold text-primary-500">{match.scoreA} - {match.scoreB}</span>
                          ) : (
                            <span className="text-xs text-foreground-400">VS</span>
                          )}
                          <span className="text-sm font-semibold text-foreground-900">{match.teamB}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-foreground-500">{match.title}</span>
                          <span className="text-xs font-medium text-foreground-600">{match.time}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Highlights for category */}
          {highlights.filter((h) => {
            const m = matches.find((mt) => mt.id === h.matchId);
            return m && m.categorySlug === activeTab;
          }).length > 0 && (
            <section className="py-8 md:py-12 px-4 md:px-6 bg-background-100">
              <div className="max-w-[1280px] mx-auto">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">{currentCategory?.name}精彩集锦</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {highlights.filter((h) => {
                    const m = matches.find((mt) => mt.id === h.matchId);
                    return m && m.categorySlug === activeTab;
                  }).map((hl) => (
                    <Link
                      key={hl.id}
                      to={`/article/${hl.matchId}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img src={hl.imageUrl} alt={hl.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute bottom-3 right-3 px-2 py-1 rounded bg-black/65 text-white text-xs font-medium">
                          <i className="ri-play-fill mr-0.5"></i>{hl.duration}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-foreground-900 leading-snug mb-2 line-clamp-2">{hl.title}</h3>
                        <div className="flex items-center justify-between text-xs text-foreground-500">
                          <span><i className="ri-eye-line mr-1"></i>{hl.views}</span>
                          <span>{hl.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}